import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import { Pressable, ScrollView, Text, View } from "react-native";
import Loader from "@/elements/components/shared/loader";
import ResponsiveContent from "@/elements/components/shared/responsive-content";
import { AppBar } from "@/elements/components/ui/app-bar";
import { api } from "@/elements/components/utils/convex-api";
import goBack from "@/elements/components/utils/go-back";

const COLLAPSED_COMMENT_LINES = 4;
const COMMENT_EXPAND_THRESHOLD = 140;

function formatReviewDate(timestamp: number) {
  return new Date(timestamp).toLocaleDateString(undefined, {
    month: "short",
    year: "numeric",
  });
}

function StarRatingDisplay({ rating }: { rating: number }) {
  return (
    <View className="flex-row items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Text
          key={star}
          className={`text-sm ${star <= rating ? "text-amber-500" : "text-gray-300"}`}
        >
          ★
        </Text>
      ))}
    </View>
  );
}

function MyReviewsScreen() {
  const [expandedReviewIds, setExpandedReviewIds] = useState<Set<string>>(
    new Set(),
  );

  const { data: reviews, isLoading } = useQuery(
    convexQuery(api.tutor.reviews.queries.getMyApprovedReviews, {}),
  );

  if (isLoading) {
    return <Loader />;
  }

  const hasReviews = (reviews?.length ?? 0) > 0;

  function toggleReview(reviewId: string) {
    setExpandedReviewIds((prev) => {
      const next = new Set(prev);
      if (next.has(reviewId)) next.delete(reviewId);
      else next.add(reviewId);
      return next;
    });
  }

  return (
    <View className="flex-1 bg-teachLoop-creamBase">
      <AppBar>
        <AppBar.Icon onPress={goBack} />
        <AppBar.Title>Back</AppBar.Title>
      </AppBar>

      <ScrollView showsVerticalScrollIndicator={false}>
        <ResponsiveContent>
          <View className="mx-5">
            <View className="h-2" />
            <View className="gap-1">
              <Text className="font-zilla-slab-medium font-medium text-dark text-[28px] leading-[1.2]">
                My reviews
              </Text>
              <Text className="font-geologica-regular font-normal text-base text-gray-800 leading-[1.5]">
                See what parents have shared about your tutoring
              </Text>
            </View>

            <View className="h-8" />

            {!hasReviews ? (
              <View className="items-center py-12 gap-2">
                <Text className="font-geologica-medium text-base text-gray-800 text-center">
                  No reviews yet
                </Text>
                <Text className="font-geologica-regular text-sm text-gray-600 text-center">
                  Approved parent reviews will appear here.
                </Text>
              </View>
            ) : (
              <View className="gap-4 pb-8">
                {reviews?.map((review) => {
                  const isExpanded = expandedReviewIds.has(review._id);
                  const canExpand =
                    review.comment.length > COMMENT_EXPAND_THRESHOLD;

                  return (
                    <Pressable
                      key={review._id}
                      disabled={!canExpand}
                      onPress={() => toggleReview(review._id)}
                      className="gap-3 rounded-xl bg-white p-4 shadow-sm"
                    >
                      <View className="flex-row items-center justify-between gap-2">
                        <StarRatingDisplay rating={review.rating} />
                        <Text className="font-geologica-regular text-xs text-gray-500">
                          {formatReviewDate(review.createdAt)}
                        </Text>
                      </View>

                      <View className="gap-1">
                        <Text
                          className="font-geologica-regular text-sm text-dark leading-[1.5]"
                          numberOfLines={
                            isExpanded ? undefined : COLLAPSED_COMMENT_LINES
                          }
                        >
                          {review.comment}
                        </Text>
                        {canExpand && (
                          <Text className="font-geologica-medium text-sm text-teal-700">
                            {isExpanded ? "Show less" : "Read more"}
                          </Text>
                        )}
                      </View>

                      <Text className="font-geologica-medium text-sm text-gray-800">
                        {review.parentDisplayName}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            )}
          </View>
        </ResponsiveContent>
      </ScrollView>
    </View>
  );
}

export default MyReviewsScreen;
